/**
 * @voltxampere/engine — Formula template: op-amp inverting (Sprint 56b)
 *
 * Subgraph: V_in ── R_in ── V_- ── R_f ── V_out; V_+ ── GND.
 *
 *     V_in ── R_in ──┬── V_- (op-amp inverting in)
 *                    └── R_f ── V_out (op-amp output)
 *     V_+ ── GND
 *
 * Closed-loop gain V_out / V_in = − R_f / R_in.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { asNumber, buildNetIndex, findVoltageSourceDriving, formatLatexValue, isGroundNet, netOf, opampTerminal, opampsInCircuit, } from "../helpers.js";
const SYMBOLIC = "\\frac{V_{out}}{V_{in}} = -\\frac{R_f}{R_{in}}";
export const opampInvertingTemplate = {
    id: "opamp-inverting",
    specificity: "high",
    displayNameKey: {
        tr: "formula.opamp-inverting.name",
        en: "formula.opamp-inverting.name",
    },
    match(circuit, selected) {
        const opamps = opampsInCircuit(circuit, selected);
        if (opamps.length === 0)
            return null;
        const netIndex = buildNetIndex(circuit);
        for (const op of opamps) {
            const inv = opampTerminal(op, "inverting");
            const noninv = opampTerminal(op, "non-inverting");
            const out = opampTerminal(op, "output");
            if (inv === null || noninv === null || out === null)
                continue;
            // Non-inverting must be grounded.
            if (!isGroundNet(circuit, noninv))
                continue;
            // Find R_f (between V_- and V_out) and R_in (between V_- and a
            // voltage source).
            const invTerminals = netIndex.get(inv) ?? [];
            const invResistors = invTerminals.filter((t) => t.component.type === "resistor");
            if (invResistors.length < 2)
                continue;
            let rf = null;
            let rin = null;
            let rinSourceNet = null;
            let vSource = null;
            for (const candidate of invResistors) {
                const other = netOf(candidate.component, "p1") === inv
                    ? netOf(candidate.component, "p2")
                    : netOf(candidate.component, "p1");
                if (other === null)
                    continue;
                if (other === out && rf === null) {
                    rf = candidate.component;
                }
                else if (rin === null) {
                    const src = findVoltageSourceDriving(circuit, other);
                    if (src !== null) {
                        rin = candidate.component;
                        rinSourceNet = other;
                        vSource = src;
                    }
                }
            }
            if (rf === null || rin === null || rinSourceNet === null || vSource === null)
                continue;
            const rfVal = asNumber(rf.params.resistance);
            const rinVal = asNumber(rin.params.resistance);
            if (rfVal === null || rinVal === null || rinVal === 0)
                continue;
            const gain = -rfVal / rinVal;
            const rfLatex = formatLatexValue(rfVal, "\\Omega");
            const rinLatex = formatLatexValue(rinVal, "\\Omega");
            const gainLatex = gain.toPrecision(3);
            return {
                templateId: "opamp-inverting",
                involvedComponents: [op.id, rf.id, rin.id, vSource.id],
                involvedNets: [inv, noninv, out, rinSourceNet],
                symbolic: SYMBOLIC,
                numeric: {
                    formula: `\\frac{V_{out}}{V_{in}} = -\\frac{${rfLatex}}{${rinLatex}} = ${gainLatex}`,
                    result: gain,
                    unit: "V/V",
                },
                specificity: "high",
            };
        }
        return null;
    },
};
//# sourceMappingURL=opamp-inverting.js.map